import { X } from 'lucide-react'
import { useState } from 'react'

import { useTags } from '@/api/hooks'
import { Combobox } from '@/components/Combobox'
import { Button } from '@/components/ui/button'
import { fr } from '@/i18n/fr'

interface Props {
  tags: string[]
  onChange: (tags: string[]) => void
}

/**
 * Tags are free text, created the moment a recipe uses one (F-13) — nothing here blocks on a
 * vocabulary lookup. The existing tags are offered as suggestions and nothing more.
 *
 * The chips and the input share one bordered box so the whole thing reads as a single field
 * rather than as a row of loose pills next to an unrelated text box (remark 1). The `×` uses
 * the `compact` button size for the same remark: at the global 44px it burst the 24px pill it
 * sits inside and shoved the label off centre.
 */
export function TagPicker({ tags, onChange }: Props) {
  const [input, setInput] = useState('')
  const { data: existingTags } = useTags()

  function addTag(raw: string) {
    const name = raw.trim()
    setInput('')
    if (!name || tags.some((tag) => tag.toLowerCase() === name.toLowerCase())) return
    onChange([...tags, name])
  }

  // Already-chosen tags drop out of the suggestions rather than being offered and ignored.
  const suggestions = (existingTags ?? []).filter(
    (tag) => !tags.some((chosen) => chosen.toLowerCase() === tag.name.toLowerCase()),
  )

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-300 p-2 focus-within:ring-2 focus-within:ring-stone-400 dark:border-stone-700">
      {tags.map((tag) => (
        <span
          key={tag}
          className="flex items-center gap-0.5 rounded-full border border-stone-300 py-0.5 pl-3 pr-1 text-sm dark:border-stone-700"
        >
          {tag}
          <Button
            type="button"
            variant="ghost"
            size="compact"
            onClick={() => onChange(tags.filter((t) => t !== tag))}
            aria-label={`${fr.common.remove} ${tag}`}
            className="rounded-full text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </span>
      ))}

      <Combobox
        value={input}
        items={suggestions}
        onChange={setInput}
        onCommit={(name) => addTag(name)}
        onKeyDown={(event) => {
          // Backspace on an empty field takes the last chip back off, which is how every
          // other chip input behaves and how these get corrected in practice.
          if (event.key === 'Backspace' && input === '' && tags.length > 0) {
            onChange(tags.slice(0, -1))
          }
        }}
        placeholder={fr.recipeEdit.tagsPlaceholder}
        ariaLabel={fr.recipeEdit.tagsLabel}
        className="min-w-40 flex-1"
        // Inside the shared box already, so the input drops its own border.
        inputClassName="border-0 px-1 focus-visible:ring-0"
      />
    </div>
  )
}
